import { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { focusElement } from '../../utils/useFocusOnOpen';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import { Code } from './cells';
import CheckForm from './quick/CheckForm';
import DepositForm from './quick/DepositForm';
import LabPhotoForm from './quick/LabPhotoForm';
import MicrographForm from './quick/MicrographForm';
import PlateStartForm from './quick/PlateStartForm';
import { AddRunForm, ExtractDnaForm } from './quick/DnaForms';
import IdentificationForm from './quick/IdentificationForm';
import { CutSlantsForm, ReviewForm, ReviveForm, SetStorageForm } from './quick/SlantForms';
import * as S from '../specimens/styles';

/*
 * The hub's quick data entry: a side drawer over the page with one small
 * form, aimed at the isolate (and plate, slant or extract) its button
 * belonged to. Nothing here leaves Home.
 *
 * The people list (/users/names) and the media (/protocols) are read once,
 * the first time the drawer opens, and kept for every later action. A check
 * or a lab photo can start from the row (every row carries its plates).
 * Everything else waits for the isolate's dossier: slant and DNA actions
 * because it carries who banked each slant -- SlantStoragePanel would
 * otherwise overwrite it -- Conditions because it carries each plate's own
 * notes, which its plate settings show and may edit (the row's plates have
 * none, and a form seeded from them would read as "no notes"), and Deposit
 * because only its plates say whether a slant set was already cut from each
 * (has_slants, by the slants' own plate): a row's slants carry only their
 * codes, and a set cut before the S-lettered codes would not show by them.
 *
 * Check, conditions, photo and micrograph forms list only plates that exist
 * on the bench: one still awaiting creation is made by "Make waiting plates".
 */
const NEEDS_DOSSIER = new Set(['setStorage', 'logReview', 'revive', 'extractDna', 'addRun', 'cutSlants', 'conditions',
  'deposit']);
const ON_MADE_PLATES = new Set(['logCheck', 'conditions', 'labPhoto', 'micrograph']);

export default function QuickActionDrawer({ action, entry, user, t, onDone, onClose, onNavigate }) {
  const [users, setUsers] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const loaded = useRef(false);
  const headingRef = useRef(null);
  const open = !!action;

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    api.get('/users/names').then((res) => setUsers(Array.isArray(res?.data) ? res.data : [])).catch(() => {});
    api.get('/protocols').then((res) => setProtocols(Array.isArray(res?.data) ? res.data : [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    focusElement(headingRef.current);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, action?.key, action?.item?.specimen_id, onClose]);

  if (!action) return null;
  const { key, item, extra = {} } = action;
  const dossier = entry?.data || null;
  const allPlates = dossier?.plates || item.plates || [];
  const plates = ON_MADE_PLATES.has(key) ? allPlates.filter((p) => p.status !== 'awaiting_creation') : allPlates;
  const slants = dossier?.slants || item.slants || [];
  const extracts = dossier?.dna || [];
  // A dossier being read again (an action just changed it) is waited for
  // too: a form opened over the old one would offer a plate whose set was
  // just cut, or miss the tubes it made.
  const waiting = NEEDS_DOSSIER.has(key) && (!dossier || !!entry?.loading);
  const props = { item, extra, user, users, protocols, plates, slants, extracts, t, onDone, onCancel: onClose, onNavigate };

  const form = () => {
    switch (key) {
      case 'logCheck': return <CheckForm {...props} />;
      case 'conditions': return <CheckForm {...props} conditionsOnly />;
      case 'labPhoto': return <LabPhotoForm {...props} />;
      case 'micrograph': return <MicrographForm {...props} />;
      case 'startPlate': return <PlateStartForm {...props} />;
      case 'activate': return <PlateStartForm {...props} activate />;
      case 'extractDna': return <ExtractDnaForm {...props} />;
      case 'addRun': return <AddRunForm {...props} />;
      case 'identify': return <IdentificationForm {...props} />;
      case 'cutSlants': return <CutSlantsForm {...props} />;
      case 'setStorage': return <SetStorageForm {...props} />;
      case 'logReview': return <ReviewForm {...props} />;
      case 'revive': return <ReviveForm {...props} />;
      case 'deposit': return <DepositForm {...props} />;
      default: return null;
    }
  };

  return (
    <div className="hub-drawer-backdrop" onClick={onClose} data-testid="hub-drawer-backdrop">
      <aside className="hub-drawer" role="dialog" aria-modal="true" aria-labelledby="hub-drawer-title"
        onClick={(e) => e.stopPropagation()} data-testid="hub-drawer">
        <div className="hub-drawer__head">
          <h3 id="hub-drawer-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {t(`action.${key}`)}
          </h3>
          <button type="button" onClick={onClose} title={t('tip.closeDrawer')} aria-label={t('tip.closeDrawer')}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--c-text-muted-fg)', lineHeight: 1 }}>
            ×
          </button>
        </div>
        <p style={{ ...S.hint, margin: '4px 0 14px' }}>
          <Code strong>{item.specimen_code}</Code>
          {item.lab_code && <> · <Code>{item.lab_code}</Code></>}
          {item.taxon_name && <> · <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{item.taxon_name}</bdi></>}
        </p>
        {waiting && !entry?.error && (
          <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />
        )}
        {waiting && entry?.error && <p role="alert" style={S.error}>{entry.error}</p>}
        {/* A new action, or the same one on another record, starts a fresh form. */}
        {!waiting && <div key={`${key}-${item.specimen_id}-${JSON.stringify(extra)}`}>{form()}</div>}
      </aside>
    </div>
  );
}
